import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Space, Input, message, Modal, 
  Popconfirm, Typography, Tooltip, DatePicker, Card,
  Select, InputNumber, Tag, ConfigProvider
} from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, 
  SearchOutlined, EyeOutlined, LinkOutlined, CopyOutlined,
  StopOutlined, PlayCircleOutlined, WarningOutlined,
  ClockCircleOutlined, CheckCircleOutlined, SyncOutlined
} from '@ant-design/icons';
import { 
  getLicenses, deleteLicense, toggleLicenseStatus, batchDisableLicenses,
  temporaryEnableLicense as enableLicenseTemporarily, checkExpiredLicenses,
  updateLicenseVersions, checkExpiredAndUpdateVersions, batchTemporaryEnableLicenses
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { generatePublicLicenseUrl, copyPublicLicenseUrl } from '../utils/licenseUtils';
import LicenseForm from '../components/LicenseForm';
import './LicenseList.css';

const { Title, Paragraph } = Typography;
const { Search } = Input;
const { MonthPicker } = DatePicker;
const { Option } = Select;

function LicenseList() {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [sharingLicense, setSharingLicense] = useState(null);
  const [newLicense, setNewLicense] = useState(null); // New state for the created license
  const [batchDisableModalVisible, setBatchDisableModalVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [temporaryEnableModalVisible, setTemporaryEnableModalVisible] = useState(false);
  const [temporaryEnableLicense, setTemporaryEnableLicense] = useState(null);
  const [tempEnableDuration, setTempEnableDuration] = useState(7);
  const [tempEnableUnit, setTempEnableUnit] = useState('days');
  const [tempEnableLoading, setTempEnableLoading] = useState(false);
  const [batchTemporaryEnableLoading, setBatchTemporaryEnableLoading] = useState(false);
  const { isAdmin } = useAuth();

  const fetchLicenses = async (page = 1, pageSize = 10, search = '') => {
    try {
      setLoading(true);
      const response = await getLicenses(page, pageSize, search);
      setLicenses(response.data.licenses);
      setPagination({
        current: response.data.pagination.page,
        pageSize: response.data.pagination.limit,
        total: response.data.pagination.total,
      });
    } catch (error) {
      message.error('获取营业执照列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 组件初始化时加载数据
    fetchLicenses(1, 10, '');
  }, []); // 只在组件挂载时执行一次

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
    fetchLicenses(newPagination.current, newPagination.pageSize, searchText);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    // 重置分页到第1页
    const newPagination = { ...pagination, current: 1 };
    setPagination(newPagination);
    fetchLicenses(1, pagination.pageSize, value);
  };

  const handleView = (record) => {
    console.log(record,"\t",record.id,"\t",record._Id)
    window.open(`/license/${record.id}`, '_blank');
  };

  const handleShareLink = (record) => {
    setSharingLicense(record);
    setShareModalVisible(true);
  };

  const handleCopyLink = async (id) => {
    try {
      const success = await copyPublicLicenseUrl(id);
      if (success) {
        message.success('公开链接已复制到剪贴板');
      } else {
        message.error('复制失败，请手动复制链接');
      }
    } catch (error) {
      console.error('复制链接时出错:', error);
      message.error('复制失败，请手动复制链接');
    }
  };

  const handleAdd = () => {
    setEditingLicense(null);
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    console.log(record)
    setEditingLicense(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    console.log(id)
    try {
      const res =  await deleteLicense(id);
      console.log(res);
      
      message.success('删除成功');
      fetchLicenses(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleToggleStatus = async (record) => {
    try {
      const response = await toggleLicenseStatus(record.id);
      message.success(response.data.message);
      fetchLicenses(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleBatchDisable = () => {
    setBatchDisableModalVisible(true);
  };

  const handleBatchDisableSubmit = async () => {
    if (!selectedDate) {
      message.error('请选择日期');
      return;
    }

    try {
      setBatchLoading(true);
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;
      const monthStr = month < 10 ? `0${month}` : `${month}`;
      const datePattern = `${year}-${monthStr}`;
      const response = await batchDisableLicenses(datePattern);
      message.success(response.data.message);
      setBatchDisableModalVisible(false);
      setSelectedDate(null);
      fetchLicenses(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error(error.response?.data?.message || '批量禁用失败');
    } finally {
      setBatchLoading(false);
    }
  };

  const handleTemporaryEnable = (record) => {
    setTemporaryEnableLicense(record);
    setTemporaryEnableModalVisible(true);
  };

  const handleTemporaryEnableSubmit = async () => {
    if (!temporaryEnableLicense) return;

    // console.log('=== 临时启用请求开始 ===');
    // console.log('营业执照:', temporaryEnableLicense);
    // console.log('时长:', tempEnableDuration);
    // console.log('单位:', tempEnableUnit);

    try {
      setTempEnableLoading(true);
      const response = await enableLicenseTemporarily(
        temporaryEnableLicense.id, 
        tempEnableDuration, 
        tempEnableUnit
      );
      // console.log('=== 临时启用响应 ===', response.data);
      message.success(response.data.message);
      setTemporaryEnableModalVisible(false);
      setTemporaryEnableLicense(null);
      fetchLicenses(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      // console.error('=== 临时启用错误 ===', error);
      console.error('错误响应:', error.response?.data);
      message.error(error.response?.data?.message || '临时启用失败');
    } finally {
      setTempEnableLoading(false);
    }
  };

  const handleCheckExpired = async () => {
    try {
      const response = await checkExpiredAndUpdateVersions();
      Modal.info({
        title: '检查结果',
        content: (
          <div>
            <p>{response.data.message}</p>
            <p>过期检查结果：{response.data.expiredResults}</p>
            <p>版本更新结果：{response.data.versionResults}</p>
            <p style={{color: '#888', fontSize: 12}}>检测时间：{response.data.checkTime}</p>
          </div>
        ),
        okText: '知道了'
      });
      fetchLicenses(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error(error.response?.data?.message || '检查过期失败');
    }
  };

  const handleUpdateVersions = async () => {
    try {
      const response = await updateLicenseVersions();
      Modal.info({
        title: '版本更新结果',
        content: (
          <div>
            <p>{response.data.message}</p>
            <p>更新数量：{response.data.updatedCount}</p>
            <p style={{color: '#888', fontSize: 12}}>更新时间：{response.data.updateTime}</p>
          </div>
        ),
        okText: '知道了'
      });
      fetchLicenses(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error(error.response?.data?.message || '版本更新失败');
    }
  };

  // 批量临时启用所有未禁用的营业执照
  const handleBatchTemporaryEnable = async () => {
    try {
      setBatchTemporaryEnableLoading(true);
      const response = await batchTemporaryEnableLicenses();
      Modal.info({
        title: '批量临时启用结果',
        content: (
          <div>
            <p style={{color: '#52c41a', fontWeight: 'bold'}}>{response.data.message}</p>
            <p>成功启用数量：{response.data.enabledCount}</p>
            {response.data.details && (
              <div style={{marginTop: 10}}>
                <p><strong>处理详情：</strong></p>
                <ul style={{paddingLeft: 20, marginTop: 5}}>
                  {response.data.details.map((detail, index) => (
                    <li key={index} style={{fontSize: 12, color: '#666'}}>
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p style={{color: '#888', fontSize: 12, marginTop: 10}}>处理时间：{response.data.processTime}</p>
          </div>
        ),
        okText: '知道了',
        width: 500
      });
      fetchLicenses(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error(error.response?.data?.message || '批量临时启用失败');
    } finally {
      setBatchTemporaryEnableLoading(false);
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingLicense(null);
  };

  const handleShareModalClose = () => {
    setShareModalVisible(false);
    setSharingLicense(null);
  };

  const handleFormSuccess = (license) => { // Pass the created license to this function
    setNewLicense(license); // Store the newly created license
    setModalVisible(false);
    setEditingLicense(null);
    fetchLicenses(pagination.current, pagination.pageSize, searchText);
  };

  const columns = [
    {
      title: '执照编号',
      dataIndex: 'number',
      key: 'number',
      ellipsis: true,
    },
    {
      title: '公司名称',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: '法定代表人',
      dataIndex: 'legalRepresentative',
      key: 'legalRepresentative',
      ellipsis: true,
      responsive: ['md'],
    },
    {
      title: '公司类型',
      dataIndex: 'type',
      key: 'type',
      ellipsis: true,
      responsive: ['lg'],
    },
    {
      title: '单位类别',
      dataIndex: 'category',
      key: 'category',
      ellipsis: true,
      responsive: ['lg'],
    },
    {
      title: '版本',
      dataIndex: 'version',
      key: 'version',
      render: (version) => {
        const color = version === 2 ? '#1890ff' : '#52c41a';
        return (
          <Tag color={color}>
            V{version || 1}
          </Tag>
        );
      },
      responsive: ['md'],
    },
    {
      title: '状态',
      dataIndex: '__v',
      key: 'status',
      render: (status, record) => {
        const isEnabled = status === 0;
        const isTemporary = record.temporaryEnableUntil !== null && record.temporaryEnableUntil !== undefined;
        const now = new Date();
        const expiryDate = isTemporary ? new Date(record.temporaryEnableUntil) : null;
        const isExpired = isTemporary && expiryDate < now;
        
        if (!isEnabled) {
          return <Tag color="red">已禁用</Tag>;
        }
        
        if (isTemporary) {
          if (isExpired) {
            return <Tag color="orange" icon={<ClockCircleOutlined />}>临时启用已过期</Tag>;
          } else {
            const remainingMs = expiryDate - now;
            const remainingDays = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));
            return (
              <Tag color="blue" icon={<ClockCircleOutlined />}>
                临时启用(剩余{remainingDays}天)
              </Tag>
            );
          }
        }
        
        return <Tag color="green" icon={<CheckCircleOutlined />}>正常</Tag>;
      },
      responsive: ['md'],
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => new Date(text).toLocaleDateString(),
      responsive: ['md'],
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" wrap>
          <Tooltip title="查看详情">
            <Button 
              type="primary" 
              icon={<EyeOutlined />} 
              onClick={() => handleView(record)}
              size="small"
            >
              查看
            </Button>
          </Tooltip>
          
          <Tooltip title="生成公开链接">
            <Button
              type="default"
              icon={<LinkOutlined />}
              onClick={() => handleShareLink(record)}
              size="small"
            >
              分享
            </Button>
          </Tooltip>
          
          {isAdmin && (
            <>
              <Button 
                type="default" 
                icon={<EditOutlined />} 
                onClick={() => handleEdit(record)}
                size="small"
              >
                编辑
              </Button>
              
              <Tooltip title={record.__v === 1 ? '启用' : '禁用'}>
                <Popconfirm
                  title={`确定要${record.__v === 1 ? '启用' : '禁用'}此营业执照吗?`}
                  onConfirm={() => handleToggleStatus(record)}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button 
                    type="default"
                    icon={record.__v === 1 ? <PlayCircleOutlined /> : <StopOutlined />}
                    size="small"
                    style={{ 
                      color: record.__v === 1 ? '#52c41a' : '#ff4d4f',
                      borderColor: record.__v === 1 ? '#52c41a' : '#ff4d4f'
                    }}
                  >
                    {record.__v === 1 ? '启用' : '禁用'}
                  </Button>
                </Popconfirm>
              </Tooltip>
              
              {record.__v === 1 && (
                <Tooltip title="临时启用">
                  <Button 
                    type="default"
                    icon={<ClockCircleOutlined />}
                    size="small"
                    onClick={() => handleTemporaryEnable(record)}
                    style={{ 
                      color: '#1890ff',
                      borderColor: '#1890ff'
                    }}
                  >
                    临时启用
                  </Button>
                </Tooltip>
              )}
              
              <Popconfirm
                title="确定要删除此营业执照吗?"
                onConfirm={() => handleDelete(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button 
                  type="default" 
                  danger 
                  icon={<DeleteOutlined />}
                  size="small"
                >
                  删除
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  // 生成分享链接的对话框
  const ShareLinkModal = () => {
    if (!sharingLicense) return null;
    
    const publicUrl = generatePublicLicenseUrl(sharingLicense.id, true);
    
    return (
      <Modal
        title="分享营业执照链接"
        open={shareModalVisible}
        onCancel={handleShareModalClose}
        footer={[
          <Button key="close" onClick={handleShareModalClose}>
            关闭
          </Button>,
          <Button 
            key="copy" 
            type="primary" 
            icon={<CopyOutlined />}
            onClick={() => handleCopyLink(sharingLicense.id)}
          >
            复制链接
          </Button>
        ]}
      >
        <div>
          <p>公司: <strong>{sharingLicense.name}</strong></p>
          <p>执照编号: {sharingLicense.number}</p>
          <div style={{ marginBottom: 8 }}>
            <strong>营业执照公开链接：</strong>
          </div>
          <Paragraph 
            copyable={{ 
              text: publicUrl,
              tooltips: ['点击复制', '复制成功!']
            }} 
            style={{ 
              background: '#f5f5f5', 
              padding: '10px', 
              borderRadius: '4px',
              wordBreak: 'break-all',
              marginBottom: 16
            }}
          >
            {publicUrl}
          </Paragraph>
          <div style={{ color: '#666', fontSize: '12px' }}>
            💡 提示：可以点击链接右侧的复制图标，或使用下方的"复制链接"按钮复制链接
          </div>
          <p style={{ marginTop: 16 }}>使用此链接可以直接查看营业执照，无需登录。</p>
        </div>
      </Modal>
    );
  };

  // New modal for displaying the created license details
  const NewLicenseModal = () => {
    if (!newLicense) return null;

    const publicUrl = generatePublicLicenseUrl(newLicense.id, true);

    return (
      <Modal
        title="新营业执照创建成功"
        open={!!newLicense}
        onCancel={() => setNewLicense(null)}
        footer={[
          <Button key="close" onClick={() => setNewLicense(null)}>
            关闭
          </Button>,
          <Button 
            key="copy" 
            type="primary" 
            icon={<CopyOutlined />}
            onClick={() => handleCopyLink(newLicense.id)}
          >
            复制链接
          </Button>
        ]}
      >
        <div>
          <p>公司: <strong>{newLicense.name}</strong></p>
          <p>执照编号: {newLicense.number}</p>
          <Paragraph 
            copyable={{ text: publicUrl }} 
            style={{ 
              background: '#f5f5f5', 
              padding: '10px', 
              borderRadius: '4px',
              wordBreak: 'break-all'
            }}
          >
            {publicUrl}
          </Paragraph>
          <p>使用此链接可以直接查看营业执照，无需登录。</p>
        </div>
      </Modal>
    );
  };

  return (
    <ConfigProvider locale={zhCN}>
      <div className="license-list-container">
      <div className="license-list-header">
        <Title level={3} className="license-list-title">营业执照管理</Title>
        <div className="license-list-actions">
          <Search
            placeholder="搜索公司名称、法定代表人或执照编号"
            onSearch={handleSearch}
            style={{ width: 300 }}
            allowClear
            enterButton={<Button icon={<SearchOutlined />}>搜索</Button>}
          />
          {isAdmin && (
            <Space>
              <Button
                type="default"
                icon={<CheckCircleOutlined />}
                onClick={handleCheckExpired}
                style={{ color: '#52c41a', borderColor: '#52c41a' }}
              >
                检查过期
              </Button>
              <Button
                type="default"
                icon={<SyncOutlined />}
                onClick={handleUpdateVersions}
                style={{ color: '#1890ff', borderColor: '#1890ff' }}
              >
                更新版本
              </Button>
              <Button
                type="default"
                icon={<ClockCircleOutlined />}
                onClick={handleBatchTemporaryEnable}
                loading={batchTemporaryEnableLoading}
                style={{ color: '#faad14', borderColor: '#faad14' }}
              >
                批量临时启用
              </Button>
              <Button
                type="default"
                icon={<WarningOutlined />}
                onClick={handleBatchDisable}
                style={{ color: '#ff4d4f', borderColor: '#ff4d4f' }}
              >
                批量禁用
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                新增营业执照
              </Button>
            </Space>
          )}
        </div>
      </div>
      
      <div className="license-table">
        <Table
          columns={columns}
          dataSource={licenses}
          rowKey="id"
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
          size="middle"
        />
      </div>

      {/* 编辑/新增表单对话框 */}
      <Modal
        title={editingLicense ? '编辑营业执照' : '新增营业执照'}
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={800}
        destroyOnHidden
        centered
      >
        <LicenseForm
          initialValues={editingLicense}
          onSuccess={handleFormSuccess}
          onCancel={handleModalClose}
        />
      </Modal>
      
      {/* 批量禁用对话框 */}
      <Modal
        title="批量禁用营业执照"
        open={batchDisableModalVisible}
        onCancel={() => {
          setBatchDisableModalVisible(false);
          setSelectedDate(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setBatchDisableModalVisible(false);
            setSelectedDate(null);
          }}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            danger
            loading={batchLoading}
            onClick={handleBatchDisableSubmit}
          >
            确认禁用
          </Button>
        ]}
      >
        <Card>
          <div style={{ marginBottom: 16 }}>
            <WarningOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
            <strong>警告：此操作将禁用指定月份创建的所有营业执照</strong>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8 }}>
              选择要禁用的月份：
            </label>
            <Select
              placeholder="选择年月"
              value={selectedDate ? selectedDate.format('YYYY-MM') : null}
              onChange={(value) => {
                if (value) {
                  const [year, month] = value.split('-');
                  const date = new Date(year, month - 1);
                  setSelectedDate(date);
                } else {
                  setSelectedDate(null);
                }
              }}
              style={{ width: '100%' }}
              allowClear
            >
              {(() => {
                const options = [];
                const currentYear = new Date().getFullYear();
                // 生成最近5年的月份选项
                for (let year = currentYear; year >= currentYear - 4; year--) {
                  for (let month = 12; month >= 1; month--) {
                    const monthStr = month < 10 ? `0${month}` : `${month}`;
                    options.push(
                      <Option key={`${year}-${monthStr}`} value={`${year}-${monthStr}`}>
                        {year}年{month}月
                      </Option>
                    );
                  }
                }
                return options;
              })()}
            </Select>
          </div>
          <div style={{ color: '#666', fontSize: '12px' }}>
            例如：选择"2024-01"将禁用2024年1月创建的所有营业执照
          </div>
        </Card>
      </Modal>
      
      {/* 临时启用对话框 */}
      <Modal
        title="临时启用营业执照"
        open={temporaryEnableModalVisible}
        onCancel={() => {
          setTemporaryEnableModalVisible(false);
          setTemporaryEnableLicense(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setTemporaryEnableModalVisible(false);
            setTemporaryEnableLicense(null);
          }}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            loading={tempEnableLoading}
            onClick={handleTemporaryEnableSubmit}
          >
            确认启用
          </Button>
        ]}
      >
        {temporaryEnableLicense && (
          <Card>
            <div style={{ marginBottom: 16 }}>
              <ClockCircleOutlined style={{ color: '#1890ff', marginRight: 8 }} />
              <strong>设置临时启用时间</strong>
            </div>
            <div style={{ marginBottom: 16 }}>
              <p><strong>公司：</strong>{temporaryEnableLicense.name}</p>
              <p><strong>执照编号：</strong>{temporaryEnableLicense.number}</p>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8 }}>
                启用时长：
              </label>
              <Input.Group compact style={{ width: '100%' }}>
                <InputNumber
                  min={1}
                  max={365}
                  value={tempEnableDuration}
                  onChange={setTempEnableDuration}
                  style={{ width: '60%' }}
                  placeholder="输入数字"
                />
                <Select
                  value={tempEnableUnit}
                  onChange={setTempEnableUnit}
                  style={{ width: '40%' }}
                >
                  <Option value="days">天</Option>
                  <Option value="weeks">周</Option>
                  <Option value="months">个月</Option>
                </Select>
              </Input.Group>
            </div>
            <div style={{ color: '#666', fontSize: '12px' }}>
              💡 提示：营业执照将在指定时间后自动禁用，无需手动操作
            </div>
          </Card>
        )}
      </Modal>
      
      {/* 分享链接对话框 */}
      <ShareLinkModal />
      {/* 新营业执照创建成功对话框 */}
      <NewLicenseModal />
    </div>
    </ConfigProvider>
  );
}

export default LicenseList; 